<script setup lang="ts">
/**
 * App-level error boundary.
 *
 * `onErrorCaptured` catches errors thrown while rendering descendants
 * (render fns, lifecycle hooks, watchers) and shows a recoverable
 * fallback instead of a blank white screen. Two escape hatches:
 *  - "Try again" clears the boundary and re-renders the subtree (good
 *    for transient failures);
 *  - "Reload page" does a hard reload (good for persistent ones).
 *
 * It also auto-clears on route change, so navigating away from a
 * broken screen recovers without a manual tap.
 *
 * Errors NOT in the render tree (async event handlers, timers) bypass
 * this — `app.config.errorHandler` in main.ts logs those as a
 * backstop.
 */
import { onErrorCaptured, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { AppErrorCard } from '@/shared/ui'

const error = ref<unknown>(null)
const route = useRoute()

onErrorCaptured((err) => {
  error.value = err
  // Returning false stops the error propagating to the global handler
  // — we're presenting a fallback, so it's "handled" here.
  return false
})

// Recover automatically when the user navigates elsewhere.
watch(
  () => route.fullPath,
  () => {
    error.value = null
  },
)

function retry() {
  error.value = null
}

function reload() {
  if (typeof window !== 'undefined') window.location.reload()
}
</script>

<template>
  <div v-if="error" class="flex min-h-screen items-center justify-center p-4 safe-top safe-bottom">
    <div class="w-full max-w-md space-y-3">
      <AppErrorCard title="Something went wrong" :error="error" @retry="retry" />
      <p class="text-center text-xs text-muted-foreground">
        If this keeps happening,
        <button
          type="button"
          class="font-medium text-primary underline-offset-2 hover:underline"
          @click="reload"
        >
          reload the page
        </button>
        .
      </p>
    </div>
  </div>
  <slot v-else />
</template>
